import { useEffect, useState, type FormEvent } from 'react'
import Field from '../components/forms/Field'
import { useNavigate } from 'react-router-dom'
import { apiJson } from '../lib/api'
import PhotoUpload from '../components/PhotoUpload'

// Edits the caller's own agent + agent_profile via PUT /api/agent.
// Prefill comes from GET /api/dashboard/agent — same composite endpoint
// the dashboard uses, so we don't add a new /api/agent GET round-trip.

interface AgentDashboardData {
  agent: {
    id: string
    name: string
    whatsapp: string | null
    fiba_license_number: string | null
    bio: string | null
    photo_url: string | null
  }
  profile: {
    slug: string
    headline: string | null
    years_experience: number | null
  } | null
  agency: { id: string; name: string } | null
}

interface FormState {
  name: string
  whatsapp: string
  agency_name: string
  fiba_license_number: string
  bio: string
  headline: string
  years_experience: string
}

function blank(): FormState {
  return {
    name: '',
    whatsapp: '',
    agency_name: '',
    fiba_license_number: '',
    bio: '',
    headline: '',
    years_experience: '',
  }
}

export default function AgentEdit() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(blank())
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const data = await apiJson<AgentDashboardData>('/api/dashboard/agent')
        if (cancelled) return
        setForm({
          name: data.agent.name,
          whatsapp: data.agent.whatsapp ?? '',
          agency_name: data.agency?.name ?? '',
          fiba_license_number: data.agent.fiba_license_number ?? '',
          bio: data.agent.bio ?? '',
          headline: data.profile?.headline ?? '',
          years_experience:
            data.profile?.years_experience != null
              ? String(data.profile.years_experience)
              : '',
        })
        setPhotoUrl(data.agent.photo_url)
        setLoading(false)
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'unknown_error'
        if (msg === 'agent_setup_required') {
          navigate('/onboarding/agent', { replace: true })
          return
        }
        if (msg === 'role_mismatch' || msg === 'unauthorized') {
          navigate('/', { replace: true })
          return
        }
        setErr(msg)
        setLoading(false)
      }
    }
    bootstrap()
    return () => {
      cancelled = true
    }
  }, [navigate])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setErr(null)
    const yearsTrimmed = form.years_experience.trim()
    const years = yearsTrimmed === '' ? null : Number(yearsTrimmed)
    if (years !== null && !Number.isFinite(years)) {
      setErr('Years of experience must be a number.')
      setSubmitting(false)
      return
    }
    try {
      await apiJson('/api/agent', {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name.trim(),
          whatsapp: form.whatsapp.trim() || null,
          agency_name: form.agency_name.trim(),
          fiba_license_number: form.fiba_license_number.trim() || null,
          bio: form.bio.trim() || null,
          headline: form.headline.trim() || null,
          years_experience: years,
        }),
      })
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-hoop-text-3">Loading…</p>
      </main>
    )
  }

  return (
    <main className="max-w-xl mx-auto px-6 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-hoop-text">
          Edit agent profile
        </h1>
        <p className="text-hoop-text-3 text-sm mt-1">
          Changes apply to the "Represented by" card across your roster.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <PhotoUpload
          entityType="agent"
          currentPhotoUrl={photoUrl}
          onChange={setPhotoUrl}
        />

        <Field label="Your name" required>
          <input
            type="text"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Headline" hint="One line — e.g. 'Independent agent · SE Asia'">
          <input
            type="text"
            value={form.headline}
            onChange={e => update('headline', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Years of experience">
          <input
            type="number"
            min={0}
            step={1}
            value={form.years_experience}
            onChange={e => update('years_experience', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Agency name" hint="Leave blank if you're independent.">
          <input
            type="text"
            value={form.agency_name}
            onChange={e => update('agency_name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="WhatsApp" hint="Include country code.">
          <input
            type="text"
            value={form.whatsapp}
            onChange={e => update('whatsapp', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field
          label="FIBA license number"
          hint="Optional. Shows a 'FIBA Licensed' badge with a link to FIBA's agent search so coaches can verify. Leave blank if you don't have one."
        >
          <input
            type="text"
            value={form.fiba_license_number}
            onChange={e => update('fiba_license_number', e.target.value)}
            placeholder="e.g. 2024-001234"
            className={inputClass}
          />
        </Field>

        <Field label="Bio">
          <textarea
            rows={4}
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            className={inputClass}
          />
        </Field>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-hoop-text-2 hover:text-hoop-text text-sm"
          >
            Cancel
          </button>
          {err && (
            <p className="text-sm text-hoop-red">{err}</p>
          )}
        </div>
      </form>
    </main>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'

